import DOMPurify from "dompurify";

// Inbound emails routinely include scripts, tracking pixels, inline <style>
// blocks that bleed into our app, target=_blank without rel=noopener, and the
// usual long tail of HTML grime. DOMPurify with a tight allowlist neutralises
// all of that. The remaining markup is restricted to the tags and attributes
// you'd reasonably want to see in a support ticket.

const ALLOWED_TAGS = [
  "a", "abbr", "b", "blockquote", "br", "caption", "code", "div",
  "em", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "img",
  "li", "ol", "p", "pre", "small", "span", "strong", "sub", "sup",
  "table", "tbody", "td", "tfoot", "th", "thead", "tr", "u", "ul",
  "figure", "figcaption",
];

const ALLOWED_ATTR = [
  "href", "src", "alt", "title", "name",
  "width", "height",
  "style", // inline styles are fine for emails; DOMPurify still strips JS in url()
  "align", "valign", "border", "cellpadding", "cellspacing",
  "colspan", "rowspan",
  "class",
];

const FORBID_TAGS = [
  "script", "style", "link", "meta", "iframe", "object", "embed",
  "form", "input", "button", "textarea", "select", "option",
];

const FORBID_ATTR = [
  "onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur",
  "onchange", "oninput", "onsubmit", "formaction",
];

let configured = false;
function configure() {
  if (configured) return;
  // Force every <a> to open in a new tab without leaking referrer/opener.
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  configured = true;
}

export function sanitizeEmailHtml(html: string): string {
  configure();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  });
}

// dotPlatformEvents caps per-property payload size, so mail bodies from
// senders like Intercom (megabytes of inline <style>) arrive with body_html
// truncated somewhere inside <head> — no <body> ever appears. Rendering
// what's left produces a blank iframe. Detect that shape and let the
// caller fall back to the plain-text body.
//
// Rule of thumb: an HTML doc with an <html> wrapper but no <body> is
// truncated. A fragment without an <html> wrapper (short reply HTML,
// signature-only, etc.) is fine as-is — real customer mail often is
// just <div>/<p>/<br> with no document skeleton.
export function isHtmlBodyUsable(html: string | null | undefined): boolean {
  if (!html) return false;
  const hasHtmlWrapper = /<html[\s>]/i.test(html);
  if (!hasHtmlWrapper) return true;
  return /<body[\s>]/i.test(html);
}
